<script>
import UserIcon from "@/components/icons/UserIcon.vue";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import PasswordIcon from "@/components/icons/PasswordIcon.vue";
import CreditCardIcon from "@/components/icons/CreditCardIcon.vue";

import Login from "@/components/login/Login.vue";
import AfterLogin from "@/components/login/AfterLogin.vue";
import ResetFeedbackMessage from "@/components/ResetFeedbackMessage.vue";

import { DateTime } from "luxon";

import { loadStripeSdk } from "vue-stripe-checkout/src/load-stripe-sdk";

import { validationMixin } from "vuelidate";

import { eurozone_countries, countries } from "@/utils/countries.js";

import "vue-select/src/scss/vue-select.scss";

import {
  required,
  requiredIf,
  minLength,
  email,
  sameAs,
} from "vuelidate/lib/validators";

import { BACKEND_URL, STRIPE } from "@/config.js";

export default {
  mixins: validationMixin,
  name: "CheckoutForm",
  page: {
    title: "Checkout",
    meta: [{ name: "description", content: "The Checkout page." }],
  },
  components: {
    UserIcon,
    CreditCardIcon,
    EmailIcon,
    PasswordIcon,
    Login,
    AfterLogin,
    ResetFeedbackMessage,
  },
  props: [
    "loadCombos",
    "guaranteeImg",
    "changeBus",
    "discount",
    "expirationDate",
    "variant",
    "bogoType",
    "threeExtraMonths",
    "paymentPlan",
    "installmentOrder",
    "includeSubscription",
    "brand"
  ],
  computed: {
    hasVat: function() {

      // console.log(
      //   "hasVat vatnumber: " +
      //     Boolean(this.form.vatNumber) +
      //     ", is valid: " +
      //     this.vatIsValid
      // );
      
      return (
        this.countryIsEurozone && (!this.form.vatNumber || !this.vatIsValid)
      );
    },
    countryIsEurozone: function() {
      if (!this.form.country) {
        return false;
      }

      // console.log(
      //   "Finding " +
      //     this.form.country +
      //     " in " +
      //     JSON.stringify(eurozone_countries)
      // );
      return (
        eurozone_countries.find(
          (country) => country.value === this.form.country
        ) != null
      );
    },

    productExpired: function() {
      if (!this.expirationDate) {
        return false;
      }
      let now = new Date();
      return now.getTime() - this.expirationDate > 0;
    },
    validForm: function() {
      if (this.$v.form.$anyError) {
        return false;
      }
      return this.paymentElementValid;
    },
    validLoginForm: function() {
      return !this.$v.formLogin.$anyError;
    },
    validRecoveryForm: function() {
      return !this.$v.formRecoverPassword.$anyError;
    },
    installmentOptions: function() {
      let options = [];
      let combo = this.selectedCombo;
      if (!combo) {
        return options;
      }

      let currency = this.currencies.find(
        (c) => c.value === this.form.currency
      );
      if (!currency) {
        return options;
      }

      if (!combo.installmentOptions) {
        return options;
      }

      let iopt = combo.installmentOptions[this.form.currency];

      iopt.sort((o1, o2) => o1.installmentCount - o2.installmentCount);

      // If paymentPlan is a number and greater than 0, slice it to limit options
      if (typeof this.paymentPlan === "number" && this.paymentPlan > 0) {
        iopt = iopt.slice(0, this.paymentPlan);
      }      

      // If 'desc', reverse it
      if (this.installmentOrder === 'desc') {
        iopt.reverse();
      }

      for (let opt of iopt) {
        let installmentWithVat = opt.installmentValue + this.vatAmountForPrice(opt.installmentValue).amount;
        let detail =
          opt.installmentCount == 1
            ? " one-time payment"
            : `1st invoice today plus 1 invoice per month for the next ${opt.installmentCount -
                1} months. Total: ${this.formatPrice(
                  opt.installmentCount * installmentWithVat
              )}`;
        options.push({
          value: opt.installmentCount,
          text: `${opt.installmentCount}x ${this.formatPrice(
            installmentWithVat
          )}`,
          detail: detail,
        });
      }

      return options;
    },
    comboSelectOptions: function() {
      let options = [];
      for (let c of this.combos) {
        const labelPriceCents = this.comboLabelPriceCents(c);
        let priceDisplay = this.formatPrice(labelPriceCents);
        let html = null;
        const name = c.name.split("-")[1];
        options.push({
          value: c.productComboId,
          html: html,
          text: name,
          price: priceDisplay,
        });
      }

      return options;
    },
    comboRadioOptions: function() {
      let options = [];
      for (let c of this.combos) {
        let period = c.period;
        const price = this.comboPrice(c);
        const labelPriceCents = this.comboLabelPriceCents(c);
        let priceDisplay = this.formatPrice(labelPriceCents);
        let html = null;
        let discountElem = "";
        if (this.discount) {
          discountElem = `<br /><span style="color: #68a432; font-weight: 600;">Recommended: ${this.discount} off<span>`;
        }
        let periodLabel = "";
        if (period === "Month") {
          discountElem = "";
          periodLabel = price.discountCents ? " 1st month" : " /mo";
        } else if (period === "Year") {
          periodLabel = price.discountCents ? " 1st year" : " /yr";
        } else {
          periodLabel = "";
        }
        let challengeOfferDescription = `Dropsmart ${c.name.toLowerCase()} including Smart Blending Program and Live Q&A. Only: `;
        let productName = this.$route.name === "SmartBlending" ? challengeOfferDescription : c.name
        html = `${productName} <span style="font-weight: 700;" class="price">${priceDisplay}${periodLabel}</span>${discountElem}`;
        let text = `${priceDisplay}${periodLabel}`;
        options.push({ value: c.productComboId, html: html, text: text });
      }

      return options;
    },
    selectedCombo: function() {
      if (!this.form.productComboId) {
        return null;
      }
      let combo = null;
      if (this.combos) {
        combo = this.combos.find(
          (c) => c.productComboId == this.form.productComboId
        );
      }
      return combo;
    },

    paymentErrorMessage: function() {
      if (!this.paymentError) {
        return null;
      }

      if (this.paymentError.code == "payment_intent_authentication_failure") {
        return "Your account is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
      }

      if (this.paymentError.code != "card_declined") {
        return this.paymentError.message;
      }

      switch (this.paymentError.decline_code) {
        case "insufficient_funds":
          return "Your account was created and it is ready, but your payment was declined due to insufficient funds. Please, Log In using the button on the top of this form and try with a different card.";
        case "authentication_required":
          return "Your account was created and it is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
        case "generic_decline":
        case "do_not_honor":
          return "Your account was created and it is ready, but your payment was declined by your card issuer. Please, contact your bank for more information, or Log In using the button on the top of this form and try again with a different card.";
        default:
          return "Your account was created and it is ready, but your payment was declined due to an unknown reason. Please, Log In using the button on the top of this form and try with a different card.";
      }
    },
  },

  data() {
    return {
      fieldRequired: "This info is required",
      countries: countries,
      errors: {
        recoveryEmail: false,
        emailIsRegistered: false,
      },
      // after the user types in the email address, an attempt will be made
      // to fetch the account data already existing for that email. If an
      // existing account is found, this object will hold the details.
      existingCustomerInfo: {
        loaded: false,
        stripeId: null,
        firstName: null,
        lastName: null,
      },
      showLoad: false,
      emailIsRegistered: false,
      vatIsValid: true,
      couponWarning: null,
      stripeKey: STRIPE.STRIPE_PUB_KEY,
      stripeElement: null,
      formDisabled: false,
      paymentComplete: false,
      paymentFailed: false,
      paymentError: null,
      selectedInstallments: 1,
      form: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        couponCode: null,
        installmentCount: 1,
        productComboId: null,
        paymentMethod: "stripe",
        acceptTerms: false,
        acceptNewsletter: false,
        periodCount: 1,
        orderUUID: null,
        currency: "eur",
        paymentIntentId: null,
        clientSecret: null,
        vatNumber: null,
        country: null,
        variant: "A",
        originUrl: "/",
        lrefereer: null,
      },
      formLogin: {
        email: null,
        password: null,
        loginAuthError: false,
      },
      formRecoverPassword: {
        email: null,
      },
      recoveryEmailWasSent: false,
      passwordRecoveryEmail: null,
      coupon: null,
      combos: [],
      currencies: [
        { value: "eur", text: "🇪🇺 EUR €", name: "Euro", symbol: "€" },
        { value: "usd", text: "🇺🇸 USD $", name: "US Dollar", symbol: "$" },
        {
          value: "brl",
          text: "🇧🇷 BRL R$",
          name: "Brazilian Real",
          symbol: "R$",
        },
        { value: "jpy", text: "🇯🇵 JPY ¥", name: "Yen", symbol: "¥" },
        { value: "gbp", text: "🇬🇧 GBP £", name: "British Pound", symbol: "£" },
        {
          value: "aud",
          text: "🇦🇺 AUD A$",
          name: "Australian Dollar",
          symbol: "A$",
        },
        { value: "cad", text: "🇨🇦 CAD $", name: "Canadian", symbol: "$" },
      ],
      stripe: null,
      paymentElementValid: false,
      cardNumberElement: null,
      cardNumberValid: false,
      cardExpiryElement: null,
      cardExpiryValid: false,
      cardCVCElement: null,
      cardCVCValid: false,
      cardPostalCodeElement: null,
      cardPostalCodeValid: false,
    };
  },
  beforeCreated() {},
  created() {
    let ps = [];
    let pageCombos = this.loadCombos;
    this.form.variant = this.variant ? this.variant : "A";

    pageCombos.forEach((id) => {
      let p = fetch(
        `${BACKEND_URL.Base}/products/combo/${id}`,
        {
          mode: "cors",
          headers: {
            Accept: "application/json",
          },
        }
      )
      .then((response) => {
          if (response.ok) {
            return response.json();
          }
        })
        .then((combo) => {
          // console.log("Response - COMBO: " + JSON.stringify(combo));
          let prices = new Map();
          combo.prices = prices;
          
          // Find the main product (the one that's NOT a subscription)
          // This is typically the OneTime product, which represents the actual combo price
          let mainProduct = combo.products.find(
            product => product.category.period === "OneTime"
          );
          
          if (!mainProduct) {
            // Fallback: if no OneTime product, use the first one
            mainProduct = combo.products[0];
          }
          
          // Use the main product's prices for all currencies
          // This gives us the correct price structure without summing individual product prices
          mainProduct.prices.forEach((price) => {
            prices.set(price.currency, {
              cents: price.cents,
              vatCents: price.vatCents,
              netCents: price.netCents,
              discountCents: price.discountCents || 0
            });
          });
          
          // Set the period based on all products
          for (let product of combo.products) {
            let period = product.category.period;
            if (period === "Monthly") {
              combo.period = "Month";
            } else if (period === "Yearly") {
              combo.period = "Year";
            }
          }
          
          if (combo.products.find((p) => p.category.period === "OneTime")) {
            combo.period = "OneTime";
          }
          
          combo.value = combo.productComboId;
          window.combo = combo;
          // console.log('Combo before returning: ' + JSON.stringify(combo));
          return combo;
        })
        .catch((networkError) => {
          console.error(networkError);
        });
      ps.push(p);
    });

    Promise.all(ps).then((combos) => {
      this.combos = combos;
      let period = this.$route.query.period;
      if (this.combos.length > 1 && period) {
        let c = this.combos.find((c) =>
          c.products.find(
            (p) =>
              p.category.period.localeCompare(period, undefined, {
                sensitivity: "accent",
              }) === 0
          )
        );
        this.form.productComboId = c
          ? c.productComboId
          : this.combos[0].productComboId;
      } else {
        this.form.productComboId = combos[0].productComboId;
      }

      let enabledCurrencies = this.currencies.filter((cur) => {
        let enabled = true;
        combos.forEach((combo) => {
          enabled = enabled && combo.prices.get(cur.value);
        });
        return enabled;
      });

      this.currencies = enabledCurrencies;

      let chosenCurrency = this.$route.query.currency;
      if (this.currencies.find((c) => c.value === chosenCurrency)) {
        this.form.currency = chosenCurrency;
      } else {
        this.form.currency = "eur";
      }
      // Always select the first option; io=asc|desc already controls ordering.
      this.selectedInstallments = this.installmentOptions[0].value;

      loadStripeSdk("v3", () => {
        const options = {
          stripeAccount: this.stripeAccount,
          apiVersion: this.apiVersion,
          locale: this.locale,
        };
        if (!this.stripe) {
          this.stripe = window.Stripe(this.stripeKey, options);
          this.setupPaymentElement();
        }
      });
    });
  },
  validations: {
    form: {
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
      },
      password: {
        required: requiredIf(function(form) {
          form;
          let isRequired = !this.existingCustomerInfo.loaded;
          return isRequired;
        }),
        minLength: minLength(6),
      },
      acceptTerms: { sameAs: sameAs(() => true) },
      vatNumber: {
        vatNumber: function(vat) {
          return this.validVatNumber(vat);
        },
      },
      country: { required },
    },
    formLogin: {
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(6),
      },
    },
    formRecoverPassword: {
      email: {
        required,
        email,
      },
    },
  },
  watch: {
    // Stripe Elements (deferred mode) must be re-synced whenever the inputs
    // it was initialised with -- currency, amount -- change. Otherwise
    // confirmPayment() rejects with "The provided currency (X) does not
    // match the expected currency (Y)" because the PaymentIntent the
    // backend creates uses the new value but Elements is still bound to
    // the old one.
    "form.currency"() {
      this.syncStripeElement();
    },
    selectedCombo() {
      this.syncStripeElement();
    },
    selectedInstallments() {
      this.syncStripeElement();
    },
  },
  methods: {
    syncStripeElement() {
      if (!this.stripeElement) {
        return;
      }
      try {
        this.stripeElement.update({
          amount: this.initialStripeAmount(),
          currency: this.form.currency,
        });
      } catch (error) {
        console.error("Failed to update Stripe Elements:", error);
      }
    },
    extractVat() {
      let useDefaultVat = true;
      let vatRate = null;
      const country = this.form.country;
      const euroCountry = eurozone_countries.find((c) => c.value === country);
      if (country && euroCountry) {
          if (useDefaultVat) {
            vatRate = 24 + "%";
        } else {
          vatRate = euroCountry.vatRate + "%";
          // console.log(vatRate);
        }
      } else {
        vatRate = 0 + "%"
      }
      return vatRate;
    },
    validVatNumber(vat) {
      return !vat || this.vatIsValid;
    },
    couponDiscountValue(combo) {
      if (this.coupon && this.form.couponCode) {
        switch (this.coupon.type) {
          case "CENTS":
            return this.coupon.moneyDiscountCents;
          case "PERCENT":
            return combo.price * (1.0 / this.coupon.percentDiscount);
        }
      }
      return 0.0;
    },
    // Estimated amount used to initialise Stripe Elements in deferred mode.
    // It only hints to Stripe at the order of magnitude / payment methods to
    // surface -- the actual amount that gets charged is determined by the
    // PaymentIntent the backend creates at submit time. We still want it to
    // be a sensible value so the Element renders the right currency-specific
    // payment methods.
    initialStripeAmount() {
      const combo = this.selectedCombo;
      if (combo && combo.prices && typeof combo.prices.get === "function") {
        const price = combo.prices.get(this.form.currency);
        if (price && price.cents) {
          return price.cents;
        }
      }
      return 100;
    },
    setupPaymentElement() {
      let v = this;
      try {
        // Deferred client_secret flow: NO PaymentIntent is created on page
        // load. We initialise Elements with mode/amount/currency so the
        // card form can render, and the real PaymentIntent is created by
        // the backend only at submit time. This eliminates the EUR 0.50
        // "Incomplete" entries that previously appeared in the Stripe
        // Payments dashboard on every checkout-page visit.
        let elements = this.stripe.elements({
          mode: "payment",
          amount: this.initialStripeAmount(),
          currency: this.form.currency,
          setupFutureUsage: "off_session",
          locale: "en",
          fonts: [
            {
              cssSrc:
                "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500",
            },
          ],
        });

        let payment = elements.create("payment", {
          fields: {
            billingDetails: {
              address: {
                postalCode: "auto",
                country: "never",
              },
            },
          },
        });
        payment.on("change", (event) => {
          v.paymentElementValid = event.complete;
        });
        payment.mount("#stripe-payment-element");
        v.stripeElement = elements;
      } catch (error) {
        console.error(error);
      }
    },
    errorDisplayFor(validity, elem) {
      let v = this;
      return (event) => {
        if (event.error) {
          elem.textContent = event.error.message;
        } else {
          elem.textContent = "";
        }
        v[validity] = event.complete;
      };
    },
    selectCurrency(currency) {
      this.form.currency = currency.value;
    },
    formatPrice(priceCents) {
      let price = (priceCents / 100).toFixed(2);
      let fmt = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.form.currency.toUpperCase(),
      });
      return fmt.format(price).replace(/(\.|,)00$/g, "");
    },
    formattedSubscriptionPrice() {
      return this.subscriptionPrice();
    },
    subscriptionPrice() {
      let combo = this.selectedCombo;
      //let product = this.combos.find(c => c.productComboId === combo.productComboId);
      if (combo) {
        return this.showPrice(combo);
      }
      return "n.a.";
    },
    showProductPrice(product) {
      if (!product) {
        return "";
      }
      let price = product.prices.find((p) => p.currency == this.form.currency);
      let bogo = this.bogoType === 'yb' ? " + 1 Year as a Bonus" : " + 1 Month as a Bonus";
      return !this.bogoType ? this.formatPrice(price.cents) : this.formatPrice(price.cents) + bogo;
    },
    hasDiscount(product) {
      if (!product || !product.prices) {
        return false;
      }
      let price = product.prices.find((p) => p.currency == this.form.currency);
      return price && price.discountCents && price.discountCents > 0;
    },
    showDiscountedPrice(combo) {
      if (!combo || !combo.products || !combo.products[0] || !combo.products[0].prices) {
        return "";
      }
      let price = combo.products[0].prices.find(
        (p) => p.currency == this.form.currency
      );
      if (!price) {
        return "";
      }
      return this.formatPrice(price.cents - price.discountCents);
    },
    showTotalDiscountedPrice(combo) {
      // Use the combo.prices Map that we set up in the fetch logic
      if (!combo || !combo.prices || typeof combo.prices.get !== "function") {
        return "";
      }
      const price = combo.prices.get(this.form.currency);
      if (!price) {
        return "";
      }

      // If installments are selected, calculate the first installment price
      const installmentOption = this.getSelectedInstallmentOption(combo);
      if (installmentOption) {
        return this.getInstallmentTotalPrice(installmentOption);
      }

      // Otherwise, show the full price
      const vat = this.vatAmountForPrice(price.cents - price.discountCents);
      return this.formatPrice(price.cents - price.discountCents + vat.amount);
    },
    comboLabelPriceCents(combo) {
      if (!combo) {
        return 0;
      }
      const paymentPlanEnabled = typeof this.paymentPlan === "number" && this.paymentPlan > 0;
      if (paymentPlanEnabled) {
        const installmentOption = this.getSelectedInstallmentOption(combo);
        if (installmentOption) {
          return installmentOption.installmentCount * installmentOption.installmentValue;
        }
      }

      const oneTimePrice = this.comboPrice(combo);
      return oneTimePrice.netCents;
    },
    showDiscountValue(combo) {
      // Use the combo.prices Map that we set up in the fetch logic
      if (!combo || !combo.prices || typeof combo.prices.get !== "function") {
        return "";
      }
      let price = combo.prices.get(this.form.currency);
      if (!price) {
        return "";
      }
      return this.formatPrice(price.discountCents);
    },
    /**
     * Gets the selected installment option for the current combo and currency
     * @param {Object} combo - The combo object
     * @returns {Object|null} The installment option or null if not found
     */
    getSelectedInstallmentOption(combo) {
      if (!combo || !combo.installmentOptions) {
        return null;
      }
      if (this.selectedInstallments > 1) {
        const installmentOption = combo.installmentOptions[this.form.currency]?.find(
          opt => opt.installmentCount === this.selectedInstallments
        );
        return installmentOption || null;
      }
      return null;
    },
    /**
     * Calculates the total price (with VAT) for an installment option
     * @param {Object} installmentOption - The installment option object
     * @returns {String} Formatted price string
     */
    getInstallmentTotalPrice(installmentOption) {
      const vat = this.vatAmountForPrice(installmentOption.installmentValue);
      return this.formatPrice(installmentOption.installmentValue + vat.amount);
    },
    /**
     * Calculates the VAT amount for an installment option
     * @param {Object} installmentOption - The installment option object
     * @returns {String} Formatted VAT amount string
     */
    getInstallmentVatPrice(installmentOption) {
      const vat = this.vatAmountForPrice(installmentOption.installmentValue);
      return this.formatPrice(vat.amount);
    },
    /**
     * Gets the net price (without VAT) for an installment option
     * @param {Object} installmentOption - The installment option object
     * @returns {String} Formatted net price string
     */
    getInstallmentNetPrice(installmentOption) {
      return this.formatPrice(installmentOption.installmentValue);
    },
    vatAmountForPrice(price) {
      const country = this.form.country;
      const euroCountry = eurozone_countries.find((c) => c.value === country);
      let vat = 0.0;
      let vatRate = "0%";
      let defaultTax = true;
      if (   (country && euroCountry && !this.form.vatNumber) 
           || country === 'EE' ) {
        if (defaultTax) {
          //if using 24% as default tax from Estonia
          vat = price * (24 / 100);
          vatRate = 24 + "%";
        } else {
          //if using customer country tax
          vat = price * (euroCountry.vatRate / 100);
          vatRate = vat + "%";
        }
      }
      return { amount: vat, vatRate: vatRate };
    },
    comboPrice(combo) {
      // The template renders before the combo data has been fetched from the
      // backend, so `combo` (typically this.selectedCombo) can be null on the
      // very first paint. Return zeros instead of throwing so Vue can render
      // the placeholder UI; once the fetch resolves the component re-renders
      // with real values.
      if (!combo || !combo.prices || typeof combo.prices.get !== "function") {
        return { cents: 0, discountCents: 0, vatRate: 0, vatCents: 0, netCents: 0 };
      }
      const price = combo.prices.get(this.form.currency);
      if (!price) {
        return { cents: 0, discountCents: 0, vatRate: 0, vatCents: 0, netCents: 0 };
      }
      const vat = this.vatAmountForPrice(price.cents - price.discountCents);

      return {
        cents: price.cents - price.discountCents + vat.amount,
        discountCents: price.discountCents,
        vatRate: vat.vatRate,
        vatCents: vat.amount,
        netCents: price.cents - price.discountCents,
      };
    },
    showPrice(combo) {
      if (!combo) {
        return "";
      }
      let price = this.comboPrice(combo);
      if (combo.period === "Month" || combo.period === "Year") {
        return `${this.formatPrice(price.cents)} / ${combo.period}`;
      } else {
        return `Total: ${this.formatPrice(price.cents)}`;
      }
    },
    showVat(combo) {
      if (!combo) {
        return "";
      }
      let price = this.comboPrice(combo);

      // If installments are selected, calculate VAT for the installment amount
      const installmentOption = this.getSelectedInstallmentOption(combo);
      if (installmentOption) {
        return this.getInstallmentVatPrice(installmentOption);
      }

      return this.formatPrice(price.vatCents);
    },
    showNetValue(combo) {
      if (!combo) {
        return "";
      }
      let price = this.comboPrice(combo);

      // If installments are selected, show the installment amount (without VAT)
      const installmentOption = this.getSelectedInstallmentOption(combo);
      if (installmentOption) {
        return this.getInstallmentNetPrice(installmentOption);
      }

      return this.formatPrice(price.netCents);
    },
    nextRenewal() {
      let combo = this.selectedCombo;
      // Guard against the initial render where the combos haven't been
      // fetched yet -- the template binds {{ nextRenewal() }} in several
      // places, so we need to return a safe placeholder string rather than
      // dereferencing a null combo.
      if (!combo) {
        return "";
      }
      let price = this.comboPrice(combo);
      let period = combo.period;
      let renewalMonths = 1;

      //console.log("nextRenewal check - Period: " + period);
      if (period === "OneTime") {
        let sub = combo.products.find((p) => p.category.period !== "OneTime");
        if (sub) {
          //console.log("sub.category.period: " + sub.category.period);
          renewalMonths = sub.category.period === "Yearly" ? 12 : 1; // Default period
          return this.calculateNextRenewal(renewalMonths, price) + " (Dropsmart subscription only)";
          //detail = " (Dropsmart subscription only)";
        } else {
          return "No renewals (payment plan only - if selected)";
        }
      } else if (period !== "OneTime") {
        renewalMonths = period === "Year" ? 12 : 1; // Default period

        // BOGO Promotion (if applicable)
        if (this.bogoType && (this.bogoType === "yb" || this.bogoType === "mb" || this.bogoType === "eoas")) {
          //console.log("Applying BOGO DISCOUNT: " + this.bogoType);
          renewalMonths = period === "Year" ? 24 : 2;
        }

        // Seventeen Months Promotion (3 extra months)
        if (this.threeExtraMonths && this.threeExtraMonths === "yes") {
          //console.log("Adding 3 Extra Months to Renewal: " + this.threeExtraMonths);
          renewalMonths += 3; // Instead of overwriting, we add the extra months
        }
        return this.calculateNextRenewal(renewalMonths, price);
      }
      return "";
    },

    calculateNextRenewal(renewalMonths, price) {
        let now = DateTime.utc();
        // Set the final period count in form (for reference)
        this.form.periodCount = renewalMonths;

        // Calculate renewal date
        let renewal = now.plus({ months: renewalMonths });

        let fmtString = renewal.toLocaleString(DateTime.DATE_MED);
        if (price.discountCents) {
          fmtString += " at " + this.formatPrice(price.netCents + price.discountCents);
        }
        if (this.hasVat) {
          fmtString += " + VAT";
        }
        return fmtString;
    },

    validateInput(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    validateLogin(name) {
      const { $dirty, $error } = this.$v.formLogin[name];
      return $dirty ? !$error : null;
    },

    validateRecoveryEmail(name) {
      const { $dirty, $error } = this.$v.formRecoverPassword[name];
      return $dirty ? !$error : null;
    },

    removeCoupon() {
      this.coupon = null;
      this.form.couponCode = null;
    },

    fetchCoupon() {
      let couponUrl =
        BACKEND_URL.Coupon + "?couponCode=" + this.form.couponCode;
      fetch(couponUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else if (response.status == 404) {
            this.couponWarning = "Coupon not found";
            return null;
          } else if (response.status == 409) {
            this.couponWarning =
              "Coupon is not available for the selected subscription";
          }
        })
        .then((coupon) => {
          this.coupon = coupon;
        })
        .catch((error) => {
          console.error(error);
          return Promise.reject();
        });
    },
    submitCheckout() {
      console.log("SUBMITING THE CHECKOUT FORM");
      this.$v.form.$touch();
      if (!this.validForm) {
        return;
      }
      this.formDisabled = true;
      this.paymentComplete = false;
      this.paymentFailed = false;
      this.$bvToast.hide("paymentErrorBox");

      let customer = sessionStorage.getItem("customerInfo");
      let jwt = null;
      let isUpgrade = false;
      if (customer) {
        jwt = JSON.parse(customer).token.token;
        isUpgrade = true;
      }
      let reqHeaders = { "Content-type": "application/json" };
      if (jwt) {
        reqHeaders["Authorization"] = jwt;
      }

      this.form.installmentCount = this.selectedInstallments;

      //set route name to apply tag
      this.form.originUrl = this.$route.name;

      //get contactId from the url
      var ontraportContactId = new URL(location.href).searchParams.get('orid');
      if( ontraportContactId != null) {
        this.form.lrefereer = ontraportContactId
        console.log("LREFEREER -> " + this.form.lrefereer)
      }

      let jsonForm = JSON.stringify(this.form);

      // Stripe deferred client_secret flow: validate the Payment Element
      // BEFORE asking the backend to create the PaymentIntent. If the form
      // is incomplete, surface the error to the customer and abort -- we
      // don't want to create a PaymentIntent we won't be able to confirm.
      this.stripeElement
        .submit()
        .then((submitResult) => {
          if (submitResult && submitResult.error) {
            this.paymentError = submitResult.error;
            this.paymentFailed = true;
            this.paymentComplete = true;
            this.$bvToast.show("paymentErrorBox");
            this.formDisabled = false;
            console.log(
              "Stripe Elements submit() failed: " +
                (submitResult.error.message || submitResult.error.code)
            );
            return Promise.reject(submitResult.error);
          }
          return fetch(BACKEND_URL.Checkout, {
            method: "POST",
            mode: "cors",
            body: jsonForm,
            headers: reqHeaders,
            credentials: "include",
            cache: "no-cache",
          });
        })
        .then((response) => {
          if (response.ok) {
            return response
              .json()
              .then((jsonData) => ({ status: "ok", data: jsonData }));
          } else {
            if (response.status == 409) {
              this.errors.emailIsRegistered = true;
              //Conflict, checkout submitted without logging in first and using an email which is already associated with an account.
              return {
                status: "error",
                data: {
                  code: "email_conflict",
                  message:
                    "The email address is already associated with an account. Please use the 'Log in' link above before completing your checkout.",
                },
              };
            } else {
              return response.text().then((textData) => ({
                status: "error",
                data: {
                  code: "generic_error",
                  message: textData,
                },
              }));
            }
          }
        })
        .then((result) => {
          if (result.status == "error") {
            this.paymentFailed = true;
            this.paymentComplete = true;
            this.paymentError = result.data;
            this.$bvToast.show("paymentErrorBox");
          } else {
            let jsonData = result.data;
            this.form.orderUUID = jsonData["order"];
            sessionStorage.setItem(
              "customerInfo",
              JSON.stringify(jsonData["customerInfo"])
            );

            let v = this;
            // Pass the clientSecret returned by the backend explicitly.
            // In deferred mode, Elements is not bound to a PaymentIntent
            // at mount time, so confirmPayment needs the secret of the
            // intent the backend just created for this order.
            this.stripe
              .confirmPayment({
                elements: this.stripeElement,
                clientSecret: jsonData.stripeSecret,
                confirmParams: {
                  return_url: window.location.toString(),
                  payment_method_data: {
                    billing_details: {
                      address: {
                        country: this.form.country,
                      },
                    },
                  },
                },
                redirect: "if_required",
              })
              .then(function(result) {
                if (result.error) {
                  v.paymentError = result.error;
                  v.paymentFailed = true;
                  v.$bvToast.show("paymentErrorBox");
                  console.log(
                    "Payment failed with error: " +
                      " code: " +
                      result.error.code +
                      ", decline_code: " +
                      result.error.decline_code +
                      ", messasge: " +
                      result.error.message +
                      ", params: " +
                      result.error.param
                  );
                } else {
                  console.log('Origin URL: ' + v.form.originUrl);
                  v.$router.push({
                    name: "ThankYou",
                    params: {
                      isUpgrade: isUpgrade,
                      isNextLevelCustomer: v.form.productComboId == 7,
                      originUrl: v.form.originUrl,
                      price: v.comboPrice(v.selectedCombo).cents,
                      vatCents: v.comboPrice(v.selectedCombo).vatCents,
                      currency: v.form.currency,
                      period: v.period,
                      country: v.form.country,
                    },
                  });
                }
                v.formDisabled = false;
                v.paymentComplete = true;
              })
              .catch((err) => console.error(err));
          }
        })
        .catch((e) => {
          console.error(e);
          this.formDisabled = false;
        });
    },
    submitLogin() {
      this.$v.formLogin.$touch();
      if (!this.validLoginForm) {
        return;
      }

      let modal = this.$refs.modalLogin;

      this.showLoad = true;
      this.formLogin.loginAuthError = false;

      fetch(BACKEND_URL.Login, {
        method: "POST",
        body: JSON.stringify(this.formLogin),
        headers: {
          "Content-type": "application/json",
        },
        credentials: "same-origin",
        cache: "no-cache",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            if (response.status == 401) {
              this.formLogin.loginAuthError = true;
            }
            throw Error(response.status + ": " + response.statusText);
          }
        })
        .then((loginResponse) => {
          this.showLoad = false;
          this.emailIsRegistered = false;
          this.formDisabled = false;
          sessionStorage.setItem("customerInfo", JSON.stringify(loginResponse));
          this.existingCustomerInfo.loaded = true;
          this.existingCustomerInfo.stripeId = loginResponse.user.stripeId;
          this.existingCustomerInfo.firstName = loginResponse.user.firstName;
          this.existingCustomerInfo.lastName = loginResponse.user.lastName;
          if (loginResponse.user.country) {
            this.existingCustomerInfo.country =
              loginResponse.user.country.isoCode;
          }
          if (loginResponse.user.vatNumber) {
            this.existingCustomerInfo.vatNumber =
              loginResponse.user.vatNumber;
          }

          this.form.firstName = loginResponse.user.firstName;
          this.form.lastName = loginResponse.user.lastName;
          this.form.email = loginResponse.user.email;
          this.form.country = this.existingCustomerInfo.country;
          this.form.vatNumber = this.existingCustomerInfo.vatNumber;
          modal.hide();
        })
        .catch((error) => {
          this.showLoad = false;
          console.error(error);
        });
    },
    resetPasswordRecovery() {
      this.recoveryEmailWasSent = false;
      this.formRecoverPassword.email = null;
      this.errors.recoveryEmail = null;
    },
    submitRecoverPassword() {
      this.$v.formRecoverPassword.$touch();
      if (!this.validRecoveryForm) {
        return;
      }

      let email = this.formRecoverPassword.email;
      let url = BACKEND_URL.RecoverPasswordInfo + "?email=" + email;
      this.showLoad = true;
      fetch(url, {
        method: "POST",
        mode: "cors",
      })
        .then((response) => {
          if (response.ok) {
            this.recoveryEmailWasSent = true;
            this.passwordRecoveryEmail = email;
            this.$refs.modalRecoverPassword.hide();
            this.$refs.modalRecoverPasswordFeedback.show();
          } else if (response.status == 404) {
            this.showLoad = false;
            this.recoveryEmailWasSent = false;
            this.errors.recoveryEmail = true;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    checkVatNumber() {
      if (!this.form.vatNumber || !this.form.country) {
        this.vatIsValid = true;
        return;
      }
      const vat = this.form.vatNumber;
      const country = this.form.country;

      let url = BACKEND_URL.CheckVat + "?vat=" + vat + "&country=" + country;

      console.log("Checking VAT number at " + url);
      fetch(url, {
        method: "get",
        mode: "cors",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          this.vatIsValid = false;
          throw new Error("Invalid request to " + url);
        })
        .then((result) => {
          console.log(JSON.stringify(result));
          this.vatIsValid = result.isValid;
          console.log("VAT is valid? " + this.vatIsValid);
        })
        .catch((error) => console.error(error))
        .finally(() => {
          this.$v.form.vatNumber.$touch();
        });
    },
    checkEmail() {
      if (!this.form.email) {
        return;
      }

      let customer = sessionStorage.getItem("customerInfo");
      if (customer) {
        let customerInfo = JSON.parse(customer);
        if (this.form.email === customerInfo.user.email) {
          this.emailIsRegistered = false;
          this.formDisabled = false;
          this.paymentError = null;
          return;
        }
      }

      let url = BACKEND_URL.CheckEmail + "?email=" + this.form.email;
      fetch(url, {
        method: "GET",
        mode: "cors",
      })
        .then((response) => {
          if (response.ok || response.status == 204) {
            this.emailIsRegistered = response.status != 204;
          } else {
            throw Error(response.status + " " + response.statusText);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    logoff() {
      this.existingCustomerInfo.loaded = false;
      this.existingCustomerInfo.firstName = null;
      this.existingCustomerInfo.lastName = null;
      this.existingCustomerInfo.email = null;
      this.existingCustomerInfo.stripeId = null;
      this.emailIsRegistered = false;
      this.form.firstName = null;
      this.form.lastName = null;
      this.form.email = null;
      this.form.password = null;
      this.$v.$reset();
      sessionStorage.removeItem("customerInfo");
    },
    say: function(message) {
      alert(message);
    },
  },
  mounted() {
    this.changeBus.$on("start-filling-form", () =>
      this.$refs["firstNameInput"].focus()
    );

    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      bvEvent, modalId;
      this.resetPasswordRecovery();
    });
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      bvEvent, modalId;
      this.resetPasswordRecovery();
    });
    this.logoff();
  },
};
</script>

<template>
  <div class="boxCheckout">
    <Login v-if="!this.existingCustomerInfo.loaded" />

    <b-modal
      class="dsModal"
      id="modalRecoverPassword"
      ref="modalRecoverPassword"
      :hide-footer="true"
      centered
      ok-only
      hide-header
    >
      <template v-slot:default>
        <h2>Lost your password?</h2>
        <p>
          Enter your email address and we'll send you a link to create a new
          password.
        </p>
        <b-form
          style="text-align: left"
          @submit.prevent="submitRecoverPassword"
          ref="recoverPasswordForm"
          aria-autocomplete="off"
        >
          <!-- E-mail Input -->
          <b-input-group class>
            <span class="formIcons email">
              <EmailIcon />
            </span>
            <b-form-input
              v-model="$v.formRecoverPassword.email.$model"
              @keypress="errors.recoveryEmail = false"
              type="email"
              name="email"
              class="LoginInput"
              size="lg"
              placeholder="E-MAIL"
              :state="validateRecoveryEmail('email')"
              aria-describedby="email-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
              >Must be a valid email address.</b-form-invalid-feedback
            >
          </b-input-group>
          <b-alert variant="error" v-model="errors.recoveryEmail"
            >This email is not associated with any account</b-alert
          >
          <button class="button-login" type="submit" id="submitRecoverPassword">
            <b-spinner v-show="showLoad"></b-spinner>
            <span id="button-text" v-if="!showLoad">Reset password</span>
          </button>
          <a href="https://dropsmarteo.com/support" target="_blank" class="support"
            >Still having problems? Click here for more information.</a
          >
        </b-form>
      </template>
    </b-modal>

    <b-modal
      class="dsModal"
      id="modalLogin"
      ref="modalLogin"
      centered
      ok-only
      no-stacking
      hide-header
    >
      <template v-slot:default>
        <h2>Log in to DROPSMART</h2>
        <b-form
          style="text-align: left"
          @submit.prevent="submitLogin"
          ref="loginForm"
          aria-autocomplete="off"
        >
          <!-- E-mail Input -->
          <b-input-group class>
            <span class="formIcons email">
              <EmailIcon />
            </span>
            <b-form-input
              v-model="$v.formLogin.email.$model"
              type="email"
              name="email"
              class="LoginInput"
              size="lg"
              placeholder="E-MAIL"
              :state="validateLogin('email')"
              aria-describedby="email-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
              >Must be a valid email address.</b-form-invalid-feedback
            >
          </b-input-group>
          <!-- Password Input -->
          <b-input-group class>
            <span class="formIcons password">
              <PasswordIcon />
            </span>
            <b-form-input
              class="LoginInput"
              size="lg"
              v-model="$v.formLogin.password.$model"
              type="password"
              name="password"
              placeholder="PASSWORD"
              :state="validateLogin('password')"
              aria-describedby="password-feedback"
            ></b-form-input>
            <b-form-invalid-feedback
              id="password-feedback"
              v-bind:tooltip="false"
              >Your password must be at least 6
              characters.</b-form-invalid-feedback
            >
          </b-input-group>
          <b-alert variant="error" :show="formLogin.loginAuthError"
            >Invalid email or password</b-alert
          >
          <button class="button-login" type="submit" id="submitLogin">
            <b-spinner v-show="showLoad"></b-spinner>
            <span class id="button-text" v-if="!showLoad">LOG IN</span>
          </button>
        </b-form>
      </template>

      <template v-slot:modal-footer>
        <a
          href="#"
          class="forgot-password"
          v-b-modal.modalRecoverPassword
          style="text-align:center;"
          >Forgot your password?</a
        >
      </template>
    </b-modal>

    <b-modal
      class="dsModal"
      id="modalRecoverPasswordFeedback"
      ref="modalRecoverPasswordFeedback"
      :hide-footer="true"
      :hide-header="true"
      centered
      ok-only
    >
      <ResetFeedbackMessage :customerEmail="passwordRecoveryEmail" />
    </b-modal>

    <AfterLogin
      v-if="this.existingCustomerInfo.loaded"
      v-bind:user="existingCustomerInfo"
      v-on:logoff="logoff"
    />
    <b-form
      style="text-align: left"
      @submit.prevent="submitCheckout"
      ref="checkoutForm"
    >
      <h2 class="form-header">Sign Up</h2>
      <!-- First Name Input -->
      <b-input-group class>
        <span class="formIcons">
          <UserIcon />
        </span>
        <b-form-input
          id="firstNameInput"
          ref="firstNameInput"
          v-model="$v.form.firstName.$model"
          name="firstName"
          class="LoginInput"
          size="lg"
          placeholder="FIRST NAME"
          :state="validateInput('firstName')"
          aria-describedby="firstName-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="firstName-feedback" v-bind:tooltip="false"
          >This is a required field.</b-form-invalid-feedback
        >
      </b-input-group>

      <!-- Last Name Input -->
      <b-input-group class>
        <span class="formIcons">
          <UserIcon />
        </span>
        <b-form-input
          v-model="$v.form.lastName.$model"
          name="lastName"
          class="LoginInput"
          size="lg"
          placeholder="LAST NAME"
          :state="validateInput('lastName')"
          aria-describedby="lastName-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="lastName-feedback" v-bind:tooltip="false"
          >This is a required field.</b-form-invalid-feedback
        >
      </b-input-group>
      <!-- E-mail Input -->
      <b-input-group class>
        <span class="formIcons email">
          <EmailIcon />
        </span>
        <b-form-input
          v-model="$v.form.email.$model"
          type="email"
          name="email"
          class="LoginInput"
          size="lg"
          placeholder="E-MAIL"
          autocomplete="new-password"
          :state="validateInput('email')"
          aria-describedby="email-feedback"
          @blur="checkEmail()"
        ></b-form-input>
        <div
          v-show="this.emailIsRegistered"
          class="invalid-feedback-custom email-exist"
        >
          This email is already linked with a Dropsmart account.
          <br />Please
          <a href="#" v-b-modal.modalLogin>sign in here</a>
        </div>
        <b-form-invalid-feedback id="email-feedback" v-bind:tooltip="false"
          >Must be a valid email address.</b-form-invalid-feedback
        >
      </b-input-group>

      <!-- Password Input -->
      <b-input-group v-show="!this.existingCustomerInfo.loaded" class>
        <span class="formIcons password">
          <PasswordIcon />
        </span>
        <b-form-input
          class="LoginInput"
          size="lg"
          v-model="$v.form.password.$model"
          type="password"
          name="password"
          autocomplete="new-password"
          placeholder="CREATE YOUR PASSWORD"
          :state="validateInput('password')"
          aria-describedby="password-feedback"
        ></b-form-input>
        <b-form-invalid-feedback id="password-feedback" v-bind:tooltip="false"
          >Your password must have at least 6 characters.</b-form-invalid-feedback
        >
      </b-input-group>

      <b-form-group
        label="Your country of origin:"
        label-for="countryInput"
        :state="validateInput('country')"
        :invalid-feedback="fieldRequired"
      >
        <b-form-select
          :options="countries"
          class="choice-selector"
          id="countryInput"
          ref="countryInput"
          v-model="$v.form.country.$model"
          size="lg"
          placeholder="COUNTRY"
          :state="validateInput('country')"
        ></b-form-select>
      </b-form-group>

      <b-form-group v-if="countryIsEurozone">
        <template v-slot:description>
          <div v-if="form.vatNumber && validVatNumber(form.vatNumber)">
            After you finish creating your account, you'll be able to provide
            additional information about your company for your Dropsmart
            invoices.
          </div>
        </template>
        <b-input-group class>
          <span class="formIcons">
            <CreditCardIcon />
          </span>
          <b-form-input
            id="vatInput"
            ref="vatInput"
            v-model="$v.form.vatNumber.$model"
            name="vatNumber"
            class="LoginInput"
            size="lg"
            placeholder="VAT NUMBER (optional)"
            :state="validateInput('vatNumber')"
            aria-describedby="vatNumber-feedback"
            @blur="checkVatNumber()"
          ></b-form-input>
          <b-form-invalid-feedback
            id="vatNumber-feedback"
            v-bind:tooltip="false"
            >Please enter a valid VAT number.</b-form-invalid-feedback
          >
        </b-input-group>
        <div
          v-show="!this.vatIsValid"
          class="invalid-feedback-custom email-exist"
        >
          This VAT number appears to be invalid.
        </div>
        <b-form-invalid-feedback id="vatNumber-feedback"
          >This does not appear to be a valid VAT
          number</b-form-invalid-feedback
        >
      </b-form-group>

      <b-card v-if="comboRadioOptions.length >= 3">
        <b-col>
          <b-row>
            <b-col class="col7"></b-col>
            <b-col class="col-5 justify-content-end">
              <b-form-select
                v-model="form.currency"
                size="sm"
                :options="currencies"
                class="currency-selector"
              ></b-form-select>
            </b-col>
          </b-row>
          <b-row style="margin-top: 1.25em; font-weight: bold">{{
            selectedCombo.name
          }}</b-row>
          <b-row>
            <b-col
              style="margin-top: 1.2em; text-align: left; padding-left: 0"
              align-self="start"
            >
              How many essential oils will you have in your account?
            </b-col>
          </b-row>
          <b-row>
            <b-input-group
              class="paymentInput"
              v-if="comboRadioOptions.length >= 3"
            >
              <b-form-select
                :searchable="false"
                :clearable="false"
                :options="comboSelectOptions"
                v-model="form.productComboId"
                :key="selectedCombo"
                :reduce="(opt) => opt.value"
                inputId="selectComboInput"
                label="text"
                class="choice-selector"
                size="lg"
                placeholder="Choose a configuration"
              >
                <template>
                  <span>${option.text}</span><small>${option.price}</small>
                </template>
              </b-form-select>
            </b-input-group>
          </b-row>
        </b-col>
      </b-card>
      <b-card
        v-if="comboRadioOptions.length < 3"
        no-body
        no-title
        style="border: 1px solid red; border-radius: 4px"
      >
        <b-card-text>
          <b-row class="options-box">
            <b-col class="col7"></b-col>
            <b-col class="col-5 justify-content-end">
              <b-form-select
                v-model="form.currency"
                size="sm"
                :options="currencies"
                class="currency-selector"
              ></b-form-select>
            </b-col>
          </b-row>
          <b-row class="options-box">
            <b-col>
              <!-- Subscription Radio Input -->
              <b-input-group class>
                <b-form-radio-group
                  class="radio-products"
                  v-model="form.productComboId"
                  :options="comboRadioOptions"
                  text-field="title"
                ></b-form-radio-group>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="first-renewal-text">
            Renewal on: {{ nextRenewal() }}.
          </b-row>
        </b-card-text>
      </b-card>
      <!-- Card Info Subscription 
      <b-card no-body v-if="form.productComboId">
        <b-card-title>
          First renewal: {{ nextRenewal() }}
          <span :class="{ stroked: variant === 'B' || this.$route.name === 'PrivateOffer' }">{{
            showProductPrice(selectedCombo.products[0])
          }}</span>
          <span v-if="variant === 'B'|| this.$route.name === 'PrivateOffer' ">{{
            showDiscountedPrice(selectedCombo)
          }}</span>
        </b-card-title>
        <b-card-text>
          <div class="first-payment-date">
            First renewal: {{ nextRenewal() }}
          </div>
          
          <div class="order-total" style="display: none">
            <span class="vat-text">
              <div class="vat-details">
                <div class="itens">
                  <p>
                    Items:
                    <span>{{ showNetValue(this.selectedCombo) }}</span>
                  </p>
                </div>
                <div class="vat">
                  <p>
                    VAT:
                    <span>{{ showVat(this.selectedCombo) }}</span>
                  </p>
                </div>
                <div v-if="this.coupon">
                  <p>
                    Discount:
                    <span>{{ showDiscountValue(this.selectedCombo) }}</span>
                  </p>
                </div>
                <div class="total">
                  <p>
                    Order Total
                    <span>{{ showPrice(this.selectedCombo) }}</span>
                  </p>
                </div>
              </div>
            </span>
          </div>
        </b-card-text>
      </b-card>
      -->

      <b-card-text>
        <div class="order-total">
          <span class="vat-text">
            <div class="vat-details">
              <div class="itens">
                <p>
                  Items:
                  <span>{{ showNetValue(this.selectedCombo) }}</span>
                </p>
              </div>
              <div class="vat">
                <p>
                  VAT {{ extractVat() }}: 
                  <span>{{ showVat(this.selectedCombo) }}</span>
                </p>
              </div>
              <div v-if="variant === 'Z'">
                <p>
                  Discount:
                  <span>{{ showDiscountValue(this.selectedCombo) }}</span>
                </p>
              </div>
              <div class="total">
                <p>
                  {{ selectedInstallments > 1 ? "Today's total order" : "Order Total" }}
                  <span>{{
                    showTotalDiscountedPrice(this.selectedCombo)
                  }}</span>
                </p>
              </div>
            </div>
          </span>
        </div>
      </b-card-text>

      <h2 class="form-header">Payment</h2>
      <div id="stripe-payment-element" ref="stripe-div"></div>

      <b-input-group class="paymentInput" v-if="this.paymentPlan && this.paymentPlan > 0 && installmentOptions.length > 1">
        <b-input-group-prepend is-text>
          <span class="card-label">
            <CreditCardIcon />
          </span>
        </b-input-group-prepend>
        <v-select
          :searchable="false"
          :clearable="false"
          :options="installmentOptions"
          v-model="selectedInstallments"
          :key="selectedInstallments"
          :reduce="(opt) => opt.value"
          inputId="selectInstallmentInput"
          label="text"
          class="installments"
          placeholder="Choose an installment plan"
        >
          <template v-slot:option="option">
            <div style="font-weight: bold">{{ option.text }}</div>
            <div style="white-space: pre-wrap; font-size: 0.8rem">
              {{ option.detail }}
            </div>
          </template>
        </v-select>
      </b-input-group>
      <!-- Image Secure SSL Encryption -->
      <img
        class="secure-ssl"
        :src="require('@/assets/images/' + this.guaranteeImg)"
        alt="Secure SSL Encryption"
      />

      <!-- Terms and Conditions accept -->
      <b-form-group class="checkout-checkbox">
        <b-form-checkbox
          id="acceptTerms"
          v-model="$v.form.acceptTerms.$model"
          name="acceptTerms"
          :state="validateInput('acceptTerms')"
          aria-describedby="terms-conditions-feedback"
        >
          I agree to {{ brand === 'MelaniMethod' ? "Melani Method's" : "Dropsmart's" }}
          <a target="_blank" href="https://dropsmarteo.com/terms-and-conditions/"
            >terms and conditions</a
          >
        </b-form-checkbox>
        <b-form-invalid-feedback
          id="terms-conditions-feedback"
          v-bind:tooltip="true"
        >
          You must agree to the Terms and Conditions in order to complete the
          checkout process.
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Newsletter accept -->
      <b-form-group class="checkout-checkbox">
        <b-form-checkbox
          id="acceptNewsletter"
          v-model="form.acceptNewsletter"
          name="acceptNewsletter"
        >
          I want to stay informed by {{ brand === 'MelaniMethod' ? "Melani Method's" : "Dropsmart's" }} newsletter
          <small>(we send content related to your subscription by email)</small>
        </b-form-checkbox>
      </b-form-group>

      <div class="textGDPR">
        <p>
          We respect the GDPR rules. Your privacy is important to us. We don't
          send spam, and you can unsubscribe at any time.
        </p>
      </div>

      <b-toast
        id="paymentErrorBox"
        variant="danger"
        static
        solid
        no-auto-hide
        no-close-button
        >{{ paymentErrorMessage }}</b-toast
      >
      <div class ref="checkoutButtonWrapper" v-if="!productExpired">
        <button
          class="button-checkout"
          type="submit"
          id="submitButton"
          :disabled="formDisabled"
        >
          <b-spinner
            v-show="formDisabled && !paymentComplete"
            id="spinner"
            label="Processing payment"
          ></b-spinner>
          <span id="button-text" v-show="paymentComplete || !formDisabled"
            >Give me instant access!</span
          >
        </button>
        <b-popover
          target="submitButton"
          triggers="click blur"
          :disabled="validForm"
          placement="top"
        >
          <template v-slot:title>Incomplete information</template>
          <span style="color: tomato; font-weight: bold">
            The information provided is incomplete. Please check all fields on
            this form, and make sure you have agreed to our Terms and
            Conditions.
          </span>
        </b-popover>
      </div>

      <b-container class="support" style="text-align: center">
        <b-row>
          <b-col>
            <div style="margin: 40px 0 0 0">
              <b>Do you have a question? Contact us.</b>
              <p>
                <a href="mailto:welcome@dropsmart.io">welcome@dropsmart.io</a>
              </p>
            </div>
          </b-col>
        </b-row>
      </b-container>
      <!-- </b-form-group> -->
    </b-form>
  </div>
</template>

<style scoped lang="scss">
$vs-state-active-bg: #dddbdb;
$vs-state-active-color: #333;
@import "vue-select/src/scss/vue-select.scss";
@font-face {
  font-family: OpenSans;
  src: url(../../assets/fonts/OpenSans-Light.ttf);
  font-weight: 200;
}

@font-face {
  font-family: OpenSans;
  src: url(../../assets/fonts/OpenSans-Regular.ttf);
  font-weight: 400;
}

@font-face {
  font-family: OpenSans;
  src: url(../../assets/fonts/OpenSans-Bold.ttf);
  font-weight: 600;
}

// Global Settings
$font-family: "OpenSans";
$font-bold: 700;

// Form Settings
$border-input: 1px solid #f7f7f7;
$bg-input: #f7f7f7;
$color-input: #000000;
$font-size-input: 18px;
$color: #000000;
$line-height-input: 23px;
$padding-input: 15px 18px 15px 55px;
$link-color: #68a432;

// Icon Settings
$icon-color: #999999;

* {
  font-family: $font-family;
}

.alert-error {
  color: #ff0000;
  font-size: 16px;
  margin: 0 !important;
  padding: 0 !important;
  text-align: center;
}

.stroked {
  text-decoration: line-through;
  margin-right: 0.5rem;
  opacity: 0.5;
}

.boxCheckout {
  background-color: #ffffff;
  border: 1px solid #aaaaaa;
  padding: 60px 15px;
  text-align: left;
  .box-login,
  .after-login {
    margin: 0 auto 30px;
    max-width: 415px;
  }
}

form {
  font-family: $font-family;
  font-size: $font-size-input;
  margin: auto;
  max-width: 415px;
  width: 100%;
  .input-group {
    margin: 0 0 8px 0;
    .formIcons {
      position: absolute;
      top: 1px;
      left: 1px;
      width: 57px;
      line-height: 53px;
      z-index: 100;
      svg {
        font-size: 16px;
        position: relative;
        z-index: 100;
        width: 57px;
        path {
          fill: $icon-color;
        }
      }
      &.email,
      &.password,
      &.coupon,
      &.creditcard {
        svg {
          font-size: 20px;
        }
      }
    }
    input {
      background: $bg-input;
      background-image: none !important;
      border: $border-input;
      border-radius: 6px !important;
      color: $color-input;
      font-size: $font-size-input;
      height: auto;
      line-height: $line-height-input;
      padding: $padding-input;
      &:focus {
        background-color: #ffffff;
        border: 1px solid #dcdcdc;
      }
    }
    #selectInstallmentInput {
      background-color: $bg-input;
      border: none;
      padding: 0;
      &:active,
      &:hover,
      &:focus,
      &:selected {
        background-color: $bg-input;
        color: $color-input;
      }
    }

    .installments {
      height: auto;
      width: 80%;
      margin: auto;
      line-height: $line-height-input;
      border: $border-input;
      border-radius: 6px !important;
    }

    .installments .vs__dropdown-toggle,
    .installments .vs__dropdown-menu {
      background: $bg-input;
      border: none;
      color: $color-input;
      li,
      span,
      div {
        &:checked {
          background-color: #fafafa;
          border: 1px solid #dcdcdc;
          color: #444;
        }
      }
    }

    .installments .vs__clear {
      display: none;
    }
    .installments .vs__open-indicator {
      fill: #394066;
    }

    .form-control {
      &:focus,
      .is-invalid,
      .is-valid {
        box-shadow: none;
      }
    }
    .invalid-feedback-custom {
      color: darkred;
    }
    .email-exist {
      display: flex;
      margin-top: 0.25rem;
      font-size: 80%;
      color: #dc3545;
      a {
        color: #509610;
      }
    }
    .discount-apply {
      align-self: center;
      color: $color;
      cursor: pointer;
      font-size: 14px;
      font-weight: $font-bold;
      line-height: 36px;
      position: absolute;
      right: 35px;
      z-index: 99;
    }
    .discount-price {
      align-self: center;
      color: $color;
      font-weight: $font-bold;
      position: absolute;
      right: 0;
      .remove-coupon {
        cursor: pointer;
        display: inline-block;
        font-size: 24px;
        padding: 0px 11px;
        transform: rotate(45deg);
        -ms-transform: rotate(45deg);
      }
    }
  }
}
form {
  .radio-products {
    display: flex;
    flex-direction: column;
    margin: 30px 0 10px;
    padding: 0 15px;
    width: 100%;
    .custom-control {
      padding-left: 35px;
    }
    input {
      &:focus ~ label {
        &::before {
          box-shadow: none !important;
        }
      }
      &:checked ~ label {
        &::after {
          top: 9px;
          left: -29px;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #ffffff !important;
        }
        &::before {
          background: $link-color !important;
        }
      }
    }
    label {
      cursor: pointer;
      margin-bottom: 12px;
      width: 100%;
      text-align: left;
      &:hover {
        &::before,
        &::after {
          background-color: #949494 !important;
        }
      }
      &::before,
      &::after {
        background-color: #d9d9d9 !important;
        border: 0;
        border-radius: 50%;
        height: 21px;
        left: -35px;
        top: 3px;
        width: 21px;
      }
      .price {
        font-weight: $font-bold;
        float: right;
      }
      .recommended {
        color: #79b867;
        font-size: 0.8em;
        font-weight: $font-bold;
      }
    }
  }
}

.row.options-box {
  min-height: 40px;
}

.row.first-renewal-text {
  min-height: 20px;
  margin-left: 20px 
}

form {
  .card {
    background-color: #ffffff;
    border: 2px solid #e6e6e6;
    border-radius: 0;
    margin: 11px 0 30px 0;
    padding: 18px;
    text-align: center;
    width: 100%;
    .card-title {
      color: $color;
      font-size: 24px;
      font-weight: 700;
      line-height: 40px;
      margin: 0;
    }
    .first-payment-date {
      font-size: 12px;
      line-height: 24px;
    }
  }
}

.order-total {
  color: $color;
  font-size: 14px;
  margin: 0;
  .underline {
    color: $link-color;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}
.vat-details {
  background: #ffffff;
  border: 1px solid #cbcbcb;
  display: block;
  padding: 15px 20px 10px;
  text-decoration: none;
  width: 100%;
  z-index: 99;
  p {
    color: $color;
    font-size: 16px;
    line-height: 30px;
    margin: 0;
    padding: 0 10px;
    text-align: left;
    width: 100%;
    &:hover {
      text-decoration: none;
    }
    span {
      color: $color;
      float: right;
    }
  }
  .total {
    border-top: 1px solid #cbcbcb;
    margin-top: 5px;
    p {
      color: $link-color;
      font-weight: 700;
      line-height: 40px;
      span {
        color: $link-color;
      }
    }
  }
}

form {
  img.payments,
  img.secure-ssl {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 80%;
  }
  img.secure-ssl {
    padding: 25px 0;
    &:after {
      content: "";
      border-bottom: 1px solid #cbcbcb;
    }
  }
  .hide-radio-payments {
    padding: 0;
    label {
      &:before,
      &:after {
        display: none;
      }
    }
  }
}

.img-payment {
  margin-bottom: 20px;
  max-width: 254px;
  width: 100%;
}

.paymentInput {
  background: #f7f7f7;
  border: 1px solid #f7f7f7;
  border-radius: 6px;
  .StripeElement--focus {
    outline: none;
    box-shadow: none;
  }
  &.input-group {
    svg {
      font-size: 20px;
      path {
        fill: #999999;
      }
    }
  }
  .input-group-text {
    background: transparent;
    border: 0;
    line-height: 41px;
    width: 57px;
  }
  .form-control {
    background: #f7f7f7 !important;
    border: 0;
    height: auto;
    line-height: 41px;
    align-self: center;
  }
  .card-label {
    text-align: center;
    width: 100%;
  }
}

//STRIPE ELEMENTS
.stripe-element-container {
  align-items: center;
  background: #f7f7f7 !important;
  display: grid;
}
.StripeElement--focus {
  box-shadow: 0;
}

//MODAL
#modalLogin,
#modalRecoverPassword {
  .modal-dialog {
    max-width: 575px;
    .modal-content {
      border: 0;
      border-radius: 0;
      h2 {
        color: #000000;
        font-size: 36px;
        font-weight: 700;
        margin-bottom: 35px;
        padding-top: 60px;
        text-align: center;
        text-transform: uppercase;
        width: 100%;
      }
      p {
        color: #000000;
        max-width: 360px;
        margin: 0 auto 25px;
        text-align: center;
        font-size: 18px;
      }
      .modal-header {
        border: 0;
        padding-top: 15px;
        flex-direction: column;
        p {
          color: #000000;
          font-size: 18px;
          margin: 0 auto;
          max-width: 360px;
          padding-top: 15px;
          text-align: center;
        }
      }
      .modal-footer {
        border: 0;
        padding-top: 0;
        padding-bottom: 65px;
        text-align: center;
      }
      .button-login {
        background: #79b867;
        border: 1px solid #79b867;
        border-radius: 50px;
        color: #ffffff;
        font-size: 20px;
        font-weight: 700;
        line-height: 53px;
        margin-top: 35px;
        padding: 0 15px;
        text-transform: uppercase;
        width: 100%;
        .spinner-border {
          vertical-align: middle;
        }
        &:hover {
          background: #000000;
          border-color: #000000;
          color: #ffffff;
        }
        &:focus {
          outline: none;
        }
      }
      .forgot-password {
        color: #666666;
        font-size: 13px;
        width: 100%;
      }
      .support {
        font-size: 14px;
        text-align: center;
        display: block;
        margin: 35px 0 30px;
        &:hover {
          color: #79b867;
          text-decoration: none;
        }
      }
    }
  }
}

#modalRecoverPassword {
  .modal-dialog {
    .modal-content {
      h2 {
        margin-bottom: 15px;
      }
      .button-login {
        margin-top: 25px;
      }
      .support {
        margin-bottom: 50px;
      }
    }
  }
}

@media screen and (max-width: 639px) {
  #modalLogin,
  #modalRecoverPassword {
    .modal-header {
      padding-top: 55px !important;
      h2 {
        font-size: 18px;
      }
    }
    .modal-footer {
      padding-top: 25px !important;
    }
    .button-login {
      font-size: 18px !important;
    }
  }
}
.currency-selector {
  border-radius: 14px;
  width: 100px;
}
.currency-selector:focus {
  box-shadow: none;
  border-color: #f0f0f0;
}
.checkout-checkbox {
  color: $color;
  font-size: 16px;
  padding: 0 15px;
  a {
    color: $link-color !important;
  }
  .custom-checkbox {
    padding-left: 35px;
    .custom-control-input.is-valid ~ .custom-control-label {
      color: $link-color;
    }
    .custom-control-label {
      cursor: pointer;
      &:hover {
        &::before {
          background: #949494;
        }
      }
      &::after,
      &::before {
        border-radius: 0;
        height: 21px;
        left: -35px;
        top: 1px;
        width: 21px;
      }
      &::before {
        background: #d9d9d9;
        border: 0;
      }
    }
    .custom-control-input:checked ~ .custom-control-label::before {
      border-color: #68a432;
      background-color: #68a432;
    }
    .custom-control-input:focus ~ .custom-control-label::before,
    .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
      border: 0;
      box-shadow: none;
    }
  }
}

.b-toast {
  width: 100%;
  max-width: 100%;
}

#paymentErrorBox {
  width: 100%;
  max-width: 100%;
  border: 0;
  background-color: #ffebeb;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  font-size: 18px;
  color: #ff0000;
  line-height: 24px;
  padding: 10px 15px;
}

.textGDPR {
  color: $color;
  font-size: 16px;
  margin: 30px 0;
  text-align: center;
}

.support {
  color: #666666;
  font-size: 16px;
  b {
    &:hover {
      color: #666666;
    }
  }
  a {
    color: #666666;
    &:hover {
      color: $link-color;
    }
  }
  &:hover {
    color: #666666;
  }
}

.logo-row {
  margin: 65px 0 0;
  .logoCheckout {
    width: 100%;
    max-width: 228px !important;
    margin: 0 auto;
  }
}

h2.form-header {
  color: $color;
  font-size: 24px;
  font-weight: $font-bold;
  margin-bottom: 15px;
  text-transform: uppercase;
}

.twoColumn {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
}
.button-checkout {
  background: #ff0000;
  color: #ffffff;
  border: none;
  border-radius: 30px;
  width: 100%;
  font-size: 20px;
  text-transform: uppercase;
  padding: 15px;
  line-height: 25px;
}

.button-checkout:hover {
  background: #89c157 !important;
}

.button-checkout:active,
.button-checkout:focus {
  background: #ff0000 !important;
  color: #ffffff !important;
  border-color: #ff0000 !important;
  box-shadow: none !important;
  outline: none;
}
.button-checkout:disabled {
  background: red !important;
  color: white;
}

/* Padding for Stripe Element containers */
.stripe-element-container {
  padding-top: 0.55rem;
  padding-bottom: 0.5rem;
}

/* Blue outline on focus */
.StripeElement--focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

/* Can't see what I type without this */
#card-number.form-control,
#card-cvc.form-control,
#card-exp.form-control {
  display: inline-block;
}
.sr-field-error {
  color: tomato;
}
.spinner,
.spinner:before,
.spinner:after {
  border-radius: 50%;
}
.spinner {
  color: maroon;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0px auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}
.spinner:before,
.spinner:after {
  position: absolute;
  content: "";
}
.spinner:before {
  width: 10.4px;
  height: 20.4px;
  background: var(--accent-color);
  border-radius: 20.4px 0 0 20.4px;
  top: -0.2px;
  left: -0.2px;
  -webkit-transform-origin: 10.4px 10.2px;
  transform-origin: 10.4px 10.2px;
  -webkit-animation: loading 2s infinite ease 1.5s;
  animation: loading 2s infinite ease 1.5s;
}
.spinner:after {
  width: 10.4px;
  height: 10.2px;
  background: var(--accent-color);
  border-radius: 0 10.2px 10.2px 0;
  top: -0.1px;
  left: 10.2px;
  -webkit-transform-origin: 0px 10.2px;
  transform-origin: 0px 10.2px;
  -webkit-animation: loading 2s infinite ease;
  animation: loading 2s infinite ease;
}

@-webkit-keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}OpenSans
.card-label {
  width: 8ch;
  text-align: right;
  font-family: "OpenSans";
}

@media screen and (max-width: 359px) {
  form {
    .radio-products {
      padding: 0;
    }
  }
}

@media screen and (max-width: 767px) {
  form {
    font-size: 16px;
    .input-group {
      input {
        font-size: 16px;
      }
      .custom-control-inline {
        margin: 0;
      }
    }
  }
  .button-checkout {
    font-size: 18px;
  }
  .checkout-checkbox {
    padding: 0;
  }
  .boxCheckout {
    border: 0;
    padding: 0;
  }

  ul.benefits {
    margin: 0 !important;
    padding: 40px 0 0 0;
  }
  ul.benefits li {
    margin: 0;
    font-size: 15px;
    line-height: 40px;
  }
  ul.benefits li svg {
    font-size: 23px;
  }
  .testimonials {
    padding: 20px 25px;
    p {
      font-size: 16px;
      line-height: 25px;
    }
  }
  .textMoneyback {
    font-size: 20px;
  }
  .text-riskFree {
    font-size: 13px;
  }
  .footer {
    padding: 60px 0 40px;
    a {
      font-size: 13px;
      margin: 0 15px;
    }
  }
}

@media screen and (max-width: 1365px) {
  form {
    .card {
      .order-total {
        .vat-text:hover {
          .vat-details {
            right: -10px;
          }
        }
      }
    }
  }
}
</style>
